import axios from 'axios';
import { API_URL } from '../global/constants';

const api = axios.create({
	baseURL: `${API_URL}/labs`,
});

export class AzureUploader {
	constructor({ file, fileName, chunkSize = 1024 * 1024 * 5 }) {
		this.chunkSize = chunkSize;
		this.file = file;
		this.fileName = fileName;
		this.blockIds = [];
		this.uploadedSize = 0;
		this.progressCache = {};
		this.activeConnections = {};
		this.onProgressFn = () => {};
		this.onErrorFn = () => {};
		this.onCompleteFn = () => {};
		this.aborted = false;
	}

	async start() {
		try {
			const ext = this.file.name.split('.').pop();
			const blobName = `${this.fileName}.${ext}`;

			// 🔐 Get SAS URL from backend
			const response = await api.post('/uploads/getAzureBlockUrls', {
				blobName: blobName,
			});

			this.sasUrl = response.data.sasUrl; // full URL with SAS
			this.blockListEndpoint = `${this.sasUrl}&comp=blocklist`;

			await this.uploadChunks();
			await this.finalizeUpload();
			this.onCompleteFn();
		} catch (error) {
			this.onErrorFn(error);
		}
	}

	async uploadChunks() {
		const totalParts = Math.ceil(this.file.size / this.chunkSize);

		for (let i = 0; i < totalParts; i++) {
			const blockId = btoa(`block-${i.toString().padStart(6, '0')}`);
			const start = i * this.chunkSize;
			const end = Math.min(this.file.size, start + this.chunkSize);
			const chunk = this.file.slice(start, end);

			await this.uploadChunk(blockId, chunk);
			this.blockIds.push(blockId);
		}
	}

	handleProgress(partIndex, event) {
		if (!this.file || !event.lengthComputable) return;

		if (event.type === 'progress' || event.type === 'error' || event.type === 'abort') {
			this.progressCache[partIndex] = event.loaded;
		}

		if (event.type === 'uploaded') {
			this.uploadedSize += this.progressCache[partIndex] || 0;
			delete this.progressCache[partIndex];
		}

		const inProgress = Object.values(this.progressCache).reduce((sum, val) => sum + val, 0);
		const sent = Math.min(this.uploadedSize + inProgress, this.file.size);
		const total = this.file.size;
		const percentage = Math.round((sent / total) * 100);

		this.onProgressFn({ sent, total, percentage });
	}

	async uploadChunk(blockId, chunk) {
		const blockUrl = `${this.sasUrl}&comp=block&blockid=${encodeURIComponent(blockId)}`;

		const xhr = new XMLHttpRequest();
		const index = this.blockIds.length;
		let settled = false;

		this.activeConnections[index] = xhr;

		return new Promise((resolve, reject) => {
			const cleanup = () => {
				delete this.activeConnections[index];
				delete this.progressCache[index];
			};

			const rejectUpload = (error) => {
				if (settled) {
					return;
				}
				settled = true;
				cleanup();
				reject(error);
			};

			const resolveUpload = () => {
				if (settled) {
					return;
				}
				settled = true;
				cleanup();
				resolve();
			};

			xhr.upload.addEventListener('progress', (e) => this.handleProgress(index, e));
			xhr.addEventListener('error', () =>
				rejectUpload(new Error('Chunk upload failed due to network error'))
			);
			xhr.addEventListener('abort', () => rejectUpload(new Error('Upload canceled')));

			xhr.onreadystatechange = () => {
				if (xhr.readyState !== 4 || settled) {
					return;
				}

				if (xhr.status === 201) {
					this.uploadedSize += chunk.size;
					this.onProgressFn({
						sent: this.uploadedSize,
						total: this.file.size,
						percentage: Math.round((this.uploadedSize / this.file.size) * 100),
					});
					resolveUpload();
					return;
				}

				rejectUpload(
					new Error(
						`Chunk upload failed with status ${xhr.status}: ${
							xhr.responseText || 'Unknown error'
						}`
					)
				);
			};

			xhr.open('PUT', blockUrl);
			xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
			xhr.send(chunk);
		});
	}

	async finalizeUpload() {
		const xml = `<?xml version="1.0" encoding="utf-8"?><BlockList>${this.blockIds
			.map((id) => `<Latest>${id}</Latest>`)
			.join('')}</BlockList>`;

		const response = await fetch(this.blockListEndpoint, {
			method: 'PUT',
			headers: {
				'Content-Type': 'application/xml',
			},
			body: xml,
		});

		if (!response.ok) {
			const errorText = await response.text();
			throw new Error(
				`Failed to finalize Azure upload (${response.status}): ${
					errorText || 'Unknown error'
				}`
			);
		}
	}

	onProgress(fn) {
		this.onProgressFn = fn;
		return this;
	}

	onError(fn) {
		this.onErrorFn = fn;
		return this;
	}

	onComplete(fn) {
		this.onCompleteFn = fn;
		return this;
	}

	abort() {
		Object.values(this.activeConnections).forEach((xhr) => xhr.abort());
		this.aborted = true;
	}
}
